import type { CreateAgentStreamEvent } from "@/types/createAgentRuntime";

/** The route adds transport failure classification to internal stream events. */
export type CreateAgentHttpStreamEvent =
  | Exclude<CreateAgentStreamEvent, { type: "error" }>
  | {
      type: "error";
      error: string;
      errorType?: "timeout" | string;
    };

/** Shared framing for the HTTP SSE adapter, browser and terminal chat lab. */
export function createAgentStreamDecoder() {
  let pending = "";
  let terminal = false;
  const decode = (frame: string): CreateAgentHttpStreamEvent | null => {
    const data = frame
      .split(/\r?\n/)
      .filter(line => line.startsWith("data:"))
      .map(line => line.slice(5).replace(/^ /, ""))
      .join("\n");
    if (!data) return null; // Comments and heartbeat frames carry no event.
    const parsed: unknown = JSON.parse(data);
    if (!parsed || typeof parsed !== "object" || !("type" in parsed)) {
      throw new Error("Invalid designer stream event");
    }
    // New nonessential event kinds can be ignored by older clients. Existing
    // event payloads retain every field, including action source and snapshots.
    if (
      !["status", "text", "stream_complete", "metadata", "error"].includes(
        String(parsed.type)
      )
    ) {
      return null;
    }
    return parsed as CreateAgentHttpStreamEvent;
  };
  return {
    get terminal() {
      return terminal;
    },
    push(chunk: string): CreateAgentHttpStreamEvent[] {
      if (terminal) return [];
      pending += chunk;
      const events: CreateAgentHttpStreamEvent[] = [];
      let separator: RegExpExecArray | null;
      while ((separator = /\r?\n\r?\n/.exec(pending))) {
        const frame = pending.slice(0, separator.index);
        pending = pending.slice(separator.index + separator[0].length);
        const event = decode(frame);
        if (event) events.push(event);
        // Metadata ends the protocol. Do not parse bytes after it: parsing the
        // whole chunk eagerly could discard an accepted response when trailing
        // data is malformed. Consumers still validate the response payload.
        if (event?.type === "metadata") {
          terminal = true;
          pending = "";
          break;
        }
      }
      return events;
    },
    finish(): CreateAgentHttpStreamEvent[] {
      if (terminal) return [];
      const frame = pending;
      pending = "";
      const event = decode(frame);
      if (event?.type === "metadata") terminal = true;
      return event ? [event] : [];
    },
  };
}

/** Release the transport when either consumer exits, errors or is cancelled. */
export async function* readCreateAgentStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal
): AsyncGenerator<CreateAgentHttpStreamEvent> {
  const reader = body.getReader();
  const utf8 = new TextDecoder();
  const decoder = createAgentStreamDecoder();
  let ended = false;
  let cancellation: Promise<void> | undefined;
  const cancel = () => {
    cancellation ??= reader.cancel().catch(() => undefined);
    return cancellation;
  };
  signal?.addEventListener("abort", cancel, { once: true });
  try {
    signal?.throwIfAborted();
    while (true) {
      const { done, value } = await reader.read();
      signal?.throwIfAborted();
      if (done) {
        ended = true;
        break;
      }
      yield* decoder.push(utf8.decode(value, { stream: true }));
      if (decoder.terminal) return;
    }
    yield* decoder.push(utf8.decode());
    yield* decoder.finish();
  } finally {
    signal?.removeEventListener("abort", cancel);
    if (!ended) await cancel();
    reader.releaseLock();
  }
}
